import React from 'react';
import { useNavigate } from 'react-router-dom';
import { soundManager } from '../utils/SoundManager';
import './JapanTrainCategories.css';

const JapanTrainCategories = () => {
    const navigate = useNavigate();

    // Japan trainer category options with traditional themes
    const japanTrainerCategories = [
        {
            id: 'japan-one-trainers',
            title: 'Tea Masters & Court Leaders',
            description: 'Train with tea ceremony masters, kabuki actors, and legendary daimyo',
            image: '/assets/images/japan-one-trainers.jpg',
            route: '/japan-one-trainers',
            color: '#8B4513', // Traditional brown
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Tea Ceremony Discipline', 'Theatrical Athletics', 'Strategic Leadership']
        },
        {
            id: 'japan-two-trainers',
            title: 'Monks & Mountain Warriors',
            description: 'Train with zen monks, giant ninjas, and sacred sumo champions',
            image: '/assets/images/japan-two-trainers.jpg',
            route: '/japan-two-trainers',
            color: '#556B2F', // Dark olive green
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Zen Meditation', 'Giant Ninja Power', 'Sacred Strength']
        },
        {
            id: 'japan-three-trainers',
            title: 'Divine Warriors & Mystics',
            description: 'Train with warrior monks, dragon lords, and imperial mystics',
            image: '/assets/images/japan-three-trainers.jpg',
            route: '/japan-three-trainers',
            color: '#4169E1', // Royal blue
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Sacred Combat', 'Divine Warfare', 'Mystical Arts']
        },
        {
            id: 'japan-four-trainers',
            title: 'Legendary Heroes & Masters',
            description: 'Train with mythical ninjas, legendary samurai, and aesthetic masters',
            image: '/assets/images/japan-four-trainers.jpg',
            route: '/japan-four-trainers',
            color: '#8B0000', // Dark red
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Mythical Techniques', 'Indestructible Defense', 'Living Artistry']
        },
        {
            id: 'japan-five-trainers',
            title: 'Sword Saints & Cultural Icons',
            description: 'Train with the greatest swordsmen and masters of cultural arts',
            image: '/assets/images/japan-five-trainers.jpg',
            route: '/japan-five-trainers',
            color: '#4B0082', // Indigo
            sound: 'gameIntroMode2',
            trainerCount: 6,
            features: ['Sword Mastery', 'Zen Archery', 'Cultural Refinement']
        }
    ];

    // Handle trainer category selection
    const handleCategorySelect = (category) => {
        console.log('Japan trainer category clicked:', category);

        // 🎵 PLAY CATEGORY-SPECIFIC SOUND
        if (category.sound) {
            soundManager.playSound(category.sound, 0.7);
        } else {
            soundManager.playButtonSound();
        }

        // Navigate to the specific Japan trainer category page
        navigate(category.route);
    };

    // Handle back to home
    const handleBackToHome = () => {
        soundManager.playButtonSound();
        navigate('/');
    };

    return (
        <div className="japan-train-categories-container">
            <div className="japan-animated-background"></div>

            <header className="japan-train-categories-header">
                <button
                    className="back-to-home-button japan-back-button"
                    onClick={handleBackToHome}
                >
                    ← Back to Home
                </button>

                <div className="header-content">
                    <h1>Ancient Japan Training Masters</h1>
                    <p>Choose your path to train with legendary warriors, monks, and cultural masters</p>
                    <div className="japan-symbol">⛩️</div>
                </div>
            </header>

            <div className="japan-trainer-categories-grid">
                {japanTrainerCategories.map((category) => (
                    <div
                        key={category.id}
                        className="japan-trainer-category-card-link"
                        onClick={() => handleCategorySelect(category)}
                    >
                        <div
                            className="japan-trainer-category-card"
                            data-category-id={category.id}
                            style={{
                                '--japan-theme-color': category.color
                            }}
                        >
                            <div className="japan-trainer-category-image">
                                <div
                                    className="japan-image-placeholder"
                                    style={{
                                        backgroundColor: category.color,
                                        backgroundImage: `url(${category.image})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center'
                                    }}
                                >
                                    <div className="japan-trainer-count-badge">
                                        {category.trainerCount} Masters
                                    </div>
                                    <div className="japan-overlay"></div>
                                </div>
                            </div>
                            <div className="japan-trainer-category-info">
                                <h2 style={{ color: category.color }}>{category.title}</h2>
                                <p>{category.description}</p>
                                <div className="japan-category-features">
                                    {category.features.map((feature, index) => (
                                        <span key={index} className="japan-feature-badge">
                                            🌸 {feature}
                                        </span>
                                    ))}
                                </div>
                                <div className="japan-theme-indicator" style={{ backgroundColor: category.color }}></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <footer className="japan-train-categories-footer">
                <div className="japan-train-info">
                    <h3>🏯 Ancient Japan Training Philosophy</h3>
                    <div className="japan-info-grid">
                        <div className="japan-info-item">
                            <span className="japan-info-icon">⚔️</span>
                            <div>
                                <strong>Legendary Masters</strong>
                                <p>Train with 30 legendary figures from Japanese history and folklore</p>
                            </div>
                        </div>
                        <div className="japan-info-item">
                            <span className="japan-info-icon">🧘</span>
                            <div>
                                <strong>Mind-Body Unity</strong>
                                <p>Experience training that harmonizes physical strength with spiritual growth</p>
                            </div>
                        </div>
                        <div className="japan-info-item">
                            <span className="japan-info-icon">🌸</span>
                            <div>
                                <strong>Cultural Heritage</strong>
                                <p>Connect with centuries of Japanese martial and cultural traditions</p>
                            </div>
                        </div>
                        <div className="japan-info-item">
                            <span className="japan-info-icon">🏮</span>
                            <div>
                                <strong>Diverse Disciplines</strong>
                                <p>From tea ceremony grace to samurai power, explore every aspect of Japanese training</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="japan-flow-info">
                    <div className="japan-divider"></div>
                    <p>💡 <strong>Path:</strong> Home → Japan Train → Masters → Trainer → Intensity → Workout</p>
                    <p className="japan-wisdom">"The way of the warrior is to stop trouble before it starts" - Miyamoto Musashi</p>
                </div>
            </footer>
        </div>
    );
};

export default JapanTrainCategories;